import type {
  EntityDisplayEntry,
  NavigationSlot,
  RouteDefinitionInput,
} from "@brains/site-composition";
import type {
  BaseEntity,
  ContentVisibility,
  IEntityService,
  ListEntitiesRequest,
} from "@brains/entity-service";
import { pluralize, type Logger } from "@brains/utils";
import type { RouteRegistry } from "./route-registry";

export type DynamicRouteEntity = Pick<BaseEntity, "id" | "metadata">;

export interface DynamicRouteEntityService extends Pick<
  IEntityService,
  "getEntityTypes"
> {
  listEntities(request: ListEntitiesRequest): Promise<DynamicRouteEntity[]>;
}

export interface DynamicRouteGeneratorServices {
  logger: Logger;
  entityService: DynamicRouteEntityService;
  listViewTemplateNames: () => string[];
}

export interface DynamicRouteGeneratorOptions {
  /**
   * Visibility scope applied to entity listings used for route generation.
   * Production site builds pass "public" so detail routes are never created
   * for non-public entities. Undefined leaves filtering to the entity service
   * default (fail-closed to "public" at the chokepoint).
   */
  visibilityScope?: ContentVisibility;
  /**
   * Filter route source entities to lifecycle-eligible published content.
   * Production site builds pass true so public drafts do not get static routes.
   */
  publishedOnly?: boolean;
}

export type DynamicRouteEntityDisplayMap = Record<string, EntityDisplayEntry>;

/**
 * Generates dynamic routes for entity types that have matching templates
 */
export class DynamicRouteGenerator {
  constructor(
    private readonly services: DynamicRouteGeneratorServices,
    private readonly routeRegistry: RouteRegistry,
    private readonly entityDisplay?: DynamicRouteEntityDisplayMap,
    private readonly options: DynamicRouteGeneratorOptions = {},
  ) {}

  private listOptions(
    extra: Omit<NonNullable<ListEntitiesRequest["options"]>, "filter"> &
      Partial<Pick<NonNullable<ListEntitiesRequest["options"]>, "filter">> = {},
  ): NonNullable<ListEntitiesRequest["options"]> {
    const scope = this.options.visibilityScope;
    const filter = extra.filter;
    return {
      ...extra,
      ...(this.options.publishedOnly && { publishedOnly: true }),
      ...(scope && {
        filter: { ...filter, visibilityScope: scope },
      }),
    };
  }

  /**
   * Generate routes for all entity types that have matching list/detail templates
   */
  async generateEntityRoutes(): Promise<void> {
    const logger = this.services.logger;

    // STEP 1: Clear all previously generated dynamic routes
    // This prevents accumulation of routes for deleted entities
    const allRoutes = this.routeRegistry.list();
    let clearedCount = 0;
    for (const route of allRoutes) {
      if (route.sourceEntityType) {
        // Dynamic routes have this marker
        this.routeRegistry.unregister(route.path);
        clearedCount++;
      }
    }

    if (clearedCount > 0) {
      logger.debug(
        `Cleared ${clearedCount} dynamic routes before regeneration`,
      );
    }

    // STEP 2: Regenerate routes from current entity state
    const entityTypes = this.services.entityService.getEntityTypes();
    logger.debug(`Found ${entityTypes.length} entity types`, { entityTypes });

    for (const entityType of entityTypes) {
      await this.generateRoutesForEntityType(entityType);
    }
  }

  /**
   * Generate routes for a specific entity type
   */
  private async generateRoutesForEntityType(entityType: string): Promise<void> {
    const logger = this.services.logger;

    // Try to find matching templates from any plugin
    const { listTemplateName, detailTemplateName } =
      this.findTemplatesForEntityType(entityType);

    if (!listTemplateName && !detailTemplateName) {
      logger.debug(
        `No matching templates found for entity type: ${entityType}`,
      );
      return;
    }

    logger.debug(`Generating routes for entity type: ${entityType}`, {
      listTemplate: listTemplateName,
      detailTemplate: detailTemplateName,
    });

    // Register index route if we have a list template
    if (listTemplateName) {
      const { pluralName, label, layout, paginate, pageSize, navigation } =
        this.getEntityDisplayConfig(entityType);

      if (paginate) {
        // Generate paginated routes
        await this.generatePaginatedRoutes(
          entityType,
          listTemplateName,
          pluralName,
          label,
          layout,
          pageSize,
          navigation,
          logger,
        );
      } else {
        // Generate a single index route (original behavior)
        const indexRoute: RouteDefinitionInput = {
          id: `${entityType}-index`,
          path: `/${pluralName}`,
          title: label,
          pageLabel: label,
          description: `Browse all ${pluralName}`,
          ...(layout && { layout }),
          navigation: {
            show: navigation.show,
            label,
            slot: navigation.slot,
            priority: navigation.priority,
          },
          sections: [
            {
              id: "list",
              template: listTemplateName,
              dataQuery: {
                entityType,
                query: { limit: 100 }, // Reasonable default limit
              },
            },
          ],
          sourceEntityType: entityType,
        };

        const existingRoute = this.routeRegistry.get(indexRoute.path);
        if (existingRoute && !existingRoute.sourceEntityType) {
          logger.debug(
            `Skipping generated index route for ${entityType}; explicit route already exists`,
            {
              path: indexRoute.path,
            },
          );
        } else {
          try {
            this.routeRegistry.register(indexRoute);
            logger.debug(`Registered index route for ${entityType}`, {
              path: indexRoute.path,
            });
          } catch (error) {
            logger.warn(`Failed to register index route for ${entityType}`, {
              error,
            });
          }
        }
      }
    }

    // Get all entities of this type and create detail routes if we have a detail template
    if (detailTemplateName) {
      try {
        const entities = await this.services.entityService.listEntities({
          entityType,
          options: this.listOptions({ limit: 1000 }),
        }); // Get all entities for static generation

        logger.debug(
          `Found ${entities.length} ${entityType} entities to generate routes for`,
        );

        // Get display config for entity type
        const { pluralName, layout } = this.getEntityDisplayConfig(entityType);

        for (const entity of entities) {
          // Use slug for URL if available (e.g., blog posts), otherwise use entity ID
          const urlSlug =
            "slug" in entity.metadata
              ? (entity.metadata["slug"] as string)
              : entity.id;

          const detailRoute: RouteDefinitionInput = {
            id: `${entityType}-${entity.id}`,
            path: `/${pluralName}/${urlSlug}`,
            title: `${this.capitalize(entityType)}: ${urlSlug}`,
            description: `View ${entityType} details`,
            ...(layout && { layout }),
            sections: [
              {
                id: "detail",
                template: detailTemplateName,
                dataQuery: {
                  entityType,
                  query: { id: urlSlug }, // Pass slug for datasource lookup
                },
              },
            ],
            sourceEntityType: entityType,
          };

          try {
            this.routeRegistry.register(detailRoute);
          } catch (error) {
            logger.warn(
              `Failed to register detail route for ${entityType}/${entity.id}`,
              { error },
            );
          }
        }

        const routeCount = listTemplateName
          ? entities.length + 1
          : entities.length;
        logger.debug(
          `Successfully registered ${routeCount} routes for ${entityType}`,
        );
      } catch (error) {
        logger.error(`Failed to list entities for type: ${entityType}`, {
          error,
        });
      }
    }
  }

  /**
   * Generate paginated list routes for an entity type
   */
  private async generatePaginatedRoutes(
    entityType: string,
    listTemplateName: string,
    pluralName: string,
    label: string,
    layout: string | undefined,
    pageSize: number,
    navigation: { show: boolean; slot: NavigationSlot; priority: number },
    logger: Logger,
  ): Promise<void> {
    // Get total entity count
    const entities = await this.services.entityService.listEntities({
      entityType,
      options: this.listOptions({ limit: 1000 }),
    });
    const totalItems = entities.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    logger.debug(
      `Generating ${totalPages} paginated routes for ${entityType}`,
      {
        totalItems,
        pageSize,
      },
    );

    // Generate route for each page
    for (let page = 1; page <= totalPages; page++) {
      const isFirstPage = page === 1;
      const path = isFirstPage
        ? `/${pluralName}`
        : `/${pluralName}/page/${page}`;

      const pageRoute: RouteDefinitionInput = {
        id: `${entityType}-index${isFirstPage ? "" : `-page-${page}`}`,
        path,
        title: isFirstPage ? label : `${label} - Page ${page}`,
        pageLabel: label,
        description: `Browse all ${pluralName}${isFirstPage ? "" : ` - Page ${page}`}`,
        ...(layout && { layout }),
        navigation: isFirstPage
          ? {
              show: navigation.show,
              label,
              slot: navigation.slot,
              priority: navigation.priority,
            }
          : undefined,
        sections: [
          {
            id: "list",
            template: listTemplateName,
            dataQuery: {
              entityType,
              query: {
                page,
                pageSize,
                baseUrl: `/${pluralName}`,
              },
            },
          },
        ],
        sourceEntityType: entityType,
      };

      const existingRoute = this.routeRegistry.get(pageRoute.path);
      if (existingRoute && !existingRoute.sourceEntityType) {
        logger.debug(
          `Skipping generated paginated route for ${entityType}; explicit route already exists`,
          { path: pageRoute.path, page },
        );
        continue;
      }

      try {
        this.routeRegistry.register(pageRoute);
        logger.debug(`Registered paginated route for ${entityType}`, {
          path: pageRoute.path,
          page,
        });
      } catch (error) {
        logger.warn(
          `Failed to register paginated route for ${entityType} page ${page}`,
          { error },
        );
      }
    }
  }

  /**
   * Find matching list and detail templates for an entity type
   */
  private findTemplatesForEntityType(entityType: string): {
    listTemplateName?: string;
    detailTemplateName?: string;
  } {
    // Get list of all templates to search through
    const allTemplateNames = this.services.listViewTemplateNames();

    // Look for templates matching the pattern [entityType]-list/detail
    // Must match exactly after namespace separator (":") to avoid
    // "social-post-list" matching entity type "post"
    let listTemplateName: string | undefined;
    let detailTemplateName: string | undefined;

    const listSuffix = `${entityType}-list`;
    const detailSuffix = `${entityType}-detail`;

    for (const templateName of allTemplateNames) {
      // Check for list template - must be exact match after ":" or at start
      if (templateName.endsWith(listSuffix)) {
        const prefix = templateName.slice(0, -listSuffix.length);
        // Valid if no prefix, or prefix ends with ":" (namespace separator)
        if (prefix === "" || prefix.endsWith(":")) {
          listTemplateName = templateName;
        }
      }

      // Check for detail template - must be exact match after ":" or at start
      if (templateName.endsWith(detailSuffix)) {
        const prefix = templateName.slice(0, -detailSuffix.length);
        // Valid if no prefix, or prefix ends with ":" (namespace separator)
        if (prefix === "" || prefix.endsWith(":")) {
          detailTemplateName = templateName;
        }
      }
    }

    // Return found templates (allow list-only or detail-only)
    const result: {
      listTemplateName?: string;
      detailTemplateName?: string;
    } = {};

    if (listTemplateName) {
      result.listTemplateName = listTemplateName;
    }
    if (detailTemplateName) {
      result.detailTemplateName = detailTemplateName;
    }

    return result;
  }

  /**
   * Get display configuration for an entity type
   * Checks custom config first, falls back to auto-generation
   * Pagination is enabled by default for all entity types with list templates
   */
  private getEntityDisplayConfig(entityType: string): {
    pluralName: string;
    label: string;
    layout?: string;
    paginate: boolean;
    pageSize: number;
    navigation: {
      show: boolean;
      slot: NavigationSlot;
      priority: number;
    };
  } {
    const config = this.entityDisplay?.[entityType];
    const DEFAULT_PAGE_SIZE = 10;
    const DEFAULT_PAGINATE = true; // Enable pagination by default
    const DEFAULT_NAV_SHOW = true;
    const DEFAULT_NAV_SLOT: NavigationSlot = "primary";
    const DEFAULT_NAV_PRIORITY = 40;

    if (config) {
      // Use custom config
      // pluralName is for URL paths (uses explicit value or pluralized label)
      // displayLabel is for navigation (always derived from label, properly pluralized)
      const pluralName =
        config.pluralName ?? pluralize(config.label.toLowerCase());
      const displayLabel = this.capitalize(
        pluralize(config.label.toLowerCase()),
      );
      return {
        pluralName,
        label: displayLabel,
        ...(config.layout && { layout: config.layout }),
        paginate: config.paginate ?? DEFAULT_PAGINATE,
        pageSize: config.pageSize ?? DEFAULT_PAGE_SIZE,
        navigation: {
          show: config.navigation?.show ?? DEFAULT_NAV_SHOW,
          slot: config.navigation?.slot ?? DEFAULT_NAV_SLOT,
          priority: config.navigation?.priority ?? DEFAULT_NAV_PRIORITY,
        },
      };
    }

    // Fall back to auto-generation with pagination enabled
    const pluralName = pluralize(entityType);
    return {
      pluralName,
      label: this.capitalize(pluralName),
      paginate: DEFAULT_PAGINATE,
      pageSize: DEFAULT_PAGE_SIZE,
      navigation: {
        show: DEFAULT_NAV_SHOW,
        slot: DEFAULT_NAV_SLOT,
        priority: DEFAULT_NAV_PRIORITY,
      },
    };
  }

  /**
   * Capitalize first letter of each word
   */
  private capitalize(str: string): string {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
}
